/**
 * Constants for WebSocket Session Hooks
 * 
 * Centralized constants for timeouts, language maps, and configuration defaults.
 * 
 * REFACTORED (2026-01): Removed orphan-related constants.
 * With the new design (messages created with server item_id), orphan cleanup is no longer needed.
 */

// ============================================================================
// Timing Constants
// ============================================================================

/** Delay before unblocking audio after interruption */
export const AUDIO_UNBLOCK_DELAY_MS = 300;

/** Delay before unblocking audio after manual cancellation */
export const CANCEL_UNBLOCK_DELAY_MS = 500;

// ============================================================================
// Retry Configuration
// ============================================================================

/** Default maximum retry attempts */
export const DEFAULT_MAX_RETRY_ATTEMPTS = 5;

/** Default base delay for retry backoff (ms) */
export const DEFAULT_RETRY_BASE_DELAY_MS = 2000;

/** Maximum retry delay cap (ms) */
export const MAX_RETRY_DELAY_MS = 30000;

/** Maximum jitter for retry delays (ms) */
export const RETRY_JITTER_MAX_MS = 500;

// ============================================================================
// Language-Specific Unintelligible Text Map
// ============================================================================

/**
 * Map of language codes to "unintelligible" text
 * Used when speech cannot be transcribed properly
 */
export const UNINTELLIGIBLE_TEXT_MAP: Record<string, string> = {
  'pt-PT': '[incompreensível]',
  'pt': '[incompreensível]',
  'en-US': '[unintelligible]',
  'en': '[unintelligible]',
  'es-ES': '[ininteligible]',
  'es': '[ininteligible]',
  'fr-FR': '[inintelligible]',
  'fr': '[inintelligible]',
  'da-DK': '[uforståelig]',
  'da': '[uforståelig]',
  'gl': '[incomprensible]', // Galician
};

/** Default language for unintelligible text */
export const DEFAULT_LANGUAGE = 'pt-PT';

/**
 * Get unintelligible text for the current language
 * Reads from localStorage or uses default
 */
export function getUnintelligibleText(): string {
  const savedLanguage = typeof window !== 'undefined'
    ? (localStorage.getItem('selected_language') || DEFAULT_LANGUAGE)
    : DEFAULT_LANGUAGE;
  
  return UNINTELLIGIBLE_TEXT_MAP[savedLanguage] 
    || UNINTELLIGIBLE_TEXT_MAP['pt'] 
    || '[unintelligible]';
}

// ============================================================================
// Hallucination Detection
// ============================================================================

/**
 * Regex patterns to detect transcription hallucinations
 * These characters indicate the transcription engine is producing garbage
 */
export const HALLUCINATION_PATTERNS = {
  japanese: /[ぁ-ゖ゛-ゟ゠-ヿ]/u,
  korean: /[\uAC00-\uD7AF]/u,
  cyrillic: /[А-Яа-яЁё]/u,
  greek: /[α-ωΑ-Ω]/u,
  arabic: /[\u0600-\u06FF]/u,
};

/**
 * Maximum length for hallucination detection
 *
 * NOTE (2026-07-07): the length cap was removed from looksLikeHallucination —
 * it let longer garbage transcripts through (the filter only fired under 20
 * chars). HALLUCINATION_MAX_LENGTH is kept for reference/back-compat but no
 * longer gates detection.
 */
export const HALLUCINATION_MAX_LENGTH = 20;

/**
 * Prompt fragments that indicate OpenAI returned system prompt text
 * instead of actual transcription (happens with gibberish input)
 */
export const PROMPT_FRAGMENTS = [
  'IMPORTANT CONTEXTUAL',
  'RULES FOR IMMERSION',
  'CONTEXTUAL CONTEXTUAL',
  'IMPORTANT CONTEXT',
];

/**
 * Check if transcript contains hallucination patterns
 */
export function hasHallucinationPatterns(transcript: string): boolean {
  return Object.values(HALLUCINATION_PATTERNS).some(pattern => 
    pattern.test(transcript)
  );
}

/**
 * Check if transcript looks like a hallucination
 *
 * NOTE (2026-07-07): the length cap (transcript.length < HALLUCINATION_MAX_LENGTH)
 * was removed — it let longer garbage transcripts through. Any transcript
 * containing a non-target script pattern is now treated as a hallucination
 * regardless of length.
 */
export function looksLikeHallucination(transcript: string): boolean {
  return hasHallucinationPatterns(transcript);
}

/**
 * Check if transcript contains prompt fragments
 */
export function looksLikePromptFragment(transcript: string): boolean {
  const upperTranscript = transcript.toUpperCase();
  return PROMPT_FRAGMENTS.some(fragment => 
    upperTranscript.includes(fragment)
  );
}

// ============================================================================
// Audio Configuration
// ============================================================================

/** Sample rate for WAV blob creation (matches OpenAI requirements) */
export const WAV_SAMPLE_RATE = 24000;

/** Number of audio channels for WAV blob */
export const WAV_NUM_CHANNELS = 1;

// ============================================================================
// ID Generation
// ============================================================================

/**
 * Generate a unique speech ID
 * Note: Still used for keyboard messages and debugging
 */
export function generateSpeechId(): string {
  return `speech_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Generate a unique keyboard message ID
 */
export function generateKeyboardMessageId(): string {
  return `keyboard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

// generateRecoveryMessageId removed - no longer needed with new design
// Messages always have valid server item_id, no recovery needed
